import DynamicSelectPicker, {
    PickerModalProps,
} from 'components/forms/form-components/select-picker/DynamicSelectPicker';
import { DynamicSelectInputProps } from 'components/forms/inputs/rhf-inputs/DynamicSelectInput';
import React, { ReactNode } from 'react';
import { FieldValues } from 'react-hook-form';
import { MinDto } from 'types/other';
import { PartyNameParty, renderPartyNumberAndIcon } from 'utils/partyNameUtil';
import { buildContactEndAdornment } from './buildContactEndAdornment';

/**
 * Contact-aware wrapper around `DynamicSelectPicker` (DynamicSelectInput + picker modal). Party
 * number + type icon on the start, `buildContactEndAdornment` on the end (combined with any
 * caller-supplied extra adornment).
 */
type ContactDynamicSelectPickerProps<
    TFormData extends FieldValues,
    TOption extends FieldValues & PartyNameParty,
    TPickedOption extends MinDto,
    TCreatedOption extends MinDto,
    TModalProps extends PickerModalProps<TPickedOption, TCreatedOption> = PickerModalProps<
        TPickedOption,
        TCreatedOption
    >,
> = Omit<
    DynamicSelectInputProps<TFormData, TOption>,
    'getOptionStartAdornment' | 'getOptionEndAdornment' | 'getOptionLabel' | 'getOptionIdentifier'
> & {
    PickerModalComponent: React.ComponentType<TModalProps>;
    isPickerModalOptionEnabled?: (option: TPickedOption | TCreatedOption) => boolean;
    /** Defaults to `"name"`. */
    getOptionLabel?: DynamicSelectInputProps<TFormData, TOption>['getOptionLabel'];
    /** Defaults to `"id"`. */
    getOptionIdentifier?: DynamicSelectInputProps<TFormData, TOption>['getOptionIdentifier'];
    /** Optional extra end adornment (e.g. `renderPartyEmail`). Rendered BEFORE the PNG chip. */
    getOptionEndAdornment?: (option: TOption) => ReactNode;
    /** The search button's own classes. */
    pickerButtonClassName?: string;
};

const ContactDynamicSelectPicker = <
    TFormData extends FieldValues,
    TOption extends FieldValues & PartyNameParty,
    TPickedOption extends MinDto,
    TCreatedOption extends MinDto,
>(
    props: ContactDynamicSelectPickerProps<TFormData, TOption, TPickedOption, TCreatedOption>
) => {
    const {
        getOptionEndAdornment: callerEndAdornment,
        getOptionLabel = 'name' as DynamicSelectInputProps<TFormData, TOption>['getOptionLabel'],
        getOptionIdentifier = 'id' as DynamicSelectInputProps<TFormData, TOption>['getOptionIdentifier'],
        ...rest
    } = props;

    const getOptionEndAdornment = (option: TOption): ReactNode => {
        const extra = callerEndAdornment ? callerEndAdornment(option) : null;
        return buildContactEndAdornment(option, extra);
    };

    return (
        <DynamicSelectPicker
            {...rest}
            getOptionLabel={getOptionLabel}
            getOptionIdentifier={getOptionIdentifier}
            getOptionStartAdornment={renderPartyNumberAndIcon}
            getOptionEndAdornment={getOptionEndAdornment}
        />
    );
};

export default ContactDynamicSelectPicker;
